import { SyntheticEvent, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Typography } from '@navi/web-ui/lib/primitives';
import { RadioGroup } from '@navi/web-ui/lib/components';
import { RootState } from '@cp/src/store';
import PrefilledTag from '../PreAudit/PrefilledTag';
import {
  AuditOption,
  ViolationQuestionProps,
  CallingAgentAuditDetails,
  AuditQuestionConfig,
  ApiAuditTrailAnswer
} from '../../interface';
import { AuditorType } from '../auditEnums';

const ViolationQuestion = (props: ViolationQuestionProps) => {
  const { question, getSelectedOption, handleSingleSelectChange, isFormDisabled, readOnly } = props;

  const auditDetails = useSelector(
    (state: RootState) => state.callAudit?.callingAgentAuditDetails
  ) as CallingAgentAuditDetails | null;

  const auditQuestionnaire = (auditDetails?.auditConfig?.auditQuestionnaire ||
    []) as AuditQuestionConfig[];
  const auditTrail = auditDetails?.auditTrail || [];

  const dsAuditTrail = useMemo(
    () => auditTrail.find(t => t.auditor === AuditorType.DS),
    [auditTrail]
  );
  const qcAgentAuditTrail = useMemo(
    () => auditTrail.find(t => t.auditor === AuditorType.QC_AGENT),
    [auditTrail]
  );
  const qcTeamLeadAuditTrail = useMemo(
    () => auditTrail.find(t => t.auditor === AuditorType.QC_TEAM_LEAD),
    [auditTrail]
  );

  const dsAnswers = (dsAuditTrail?.answers || []) as unknown as ApiAuditTrailAnswer[];
  const qcAgentAnswers = (qcAgentAuditTrail?.answers || []) as unknown as ApiAuditTrailAnswer[];

  const getAnswerValue = (
    questionId: number,
    answersSource: ApiAuditTrailAnswer[]
  ): string | null => {
    const answer = answersSource.find((a: ApiAuditTrailAnswer) => a.question_id === questionId);
    if (!answer) return null;
    const questionConfig = auditQuestionnaire.find(q => q.question_id === questionId);
    if (!questionConfig) return null;
    const selectedOption = questionConfig.options.find(opt =>
      answer.selected_option_ids.includes(opt.option_id)
    );
    return selectedOption?.option_text || null;
  };

  const getPreAuditValue = (questionId: number): string | null =>
    getAnswerValue(questionId, dsAnswers);

  const getQcAgentValue = (questionId: number): string | null =>
    getAnswerValue(questionId, qcAgentAnswers);

  const getRadioOptions = (options: AuditOption[]) =>
    options.map(opt => ({
      id: String(opt.option_id),
      label: opt.option_text,
      value: String(opt.option_id)
    }));

  return (
    <div className="p-3 border-t border-0 border-dotted border-[var(--greyscale-light)]">
      <Typography variant="h4" color="var(--grayscale-2)">
        {question.question_text}
      </Typography>
      <RadioGroup
        name={`question-${question.question_id}`}
        value={getSelectedOption(question.question_id)}
        onChange={(event: SyntheticEvent) => {
          const value = (event?.target as HTMLInputElement)?.value;
          handleSingleSelectChange(question.question_id, parseInt(value, 10));
        }}
        options={getRadioOptions(question.options)}
        disabled={isFormDisabled}
      />
      <div className="flex flex-row gap-2 flex-wrap">
        {!readOnly &&
          dsAuditTrail &&
          (qcAgentAuditTrail || qcTeamLeadAuditTrail) &&
          getPreAuditValue(question.question_id) && (
            <PrefilledTag
              preAuditDoneBy={dsAuditTrail.auditor}
              preAuditText={getPreAuditValue(question.question_id) || ''}
            />
          )}
        {!readOnly &&
          qcAgentAuditTrail &&
          qcTeamLeadAuditTrail &&
          getQcAgentValue(question.question_id) && (
            <PrefilledTag
              preAuditDoneBy={qcAgentAuditTrail.auditor}
              preAuditText={getQcAgentValue(question.question_id) || ''}
            />
          )}
      </div>
    </div>
  );
};

export default ViolationQuestion;
